import { shopifyApolloClient } from "@clients/shopify-client";
import { Shopify } from "@models/shopify.model";
import { ShopifyToken } from "@providers/profile/ProfileProvider.model";
import { getNodes } from "@utils/getNodes";
import { createContext, Dispatch, ReactNode, useContext, useEffect, useReducer } from "react";
import {
  OrderAction,
  OrderContext,
  OrderDispatch,
  OrderProviderState,
  ORDER_QUERY,
} from "./OrderProvider.model";

const OrderContext = createContext<OrderContext>(null);

async function getOrders(shopifyToken: ShopifyToken, dispatch: Dispatch<OrderDispatch>) {
  try {
    const { data: response } = await shopifyApolloClient.query<{
      customer: { orders: Shopify.Connection<Shopify.Order> };
    }>({
      query: ORDER_QUERY,
      variables: { accessToken: shopifyToken.value.access_token },
    });

    if (!response) throw new Error("Orders could not be fetched.");
    const orders = getNodes<Shopify.Order>(response.customer.orders);
    dispatch({ type: OrderAction.SET_ORDERS, orders });
    return orders;
  } catch (e) {
    console.error(e);
    dispatch({ type: OrderAction.FAILED_FETCH, message: e });
  }
}

function orderReducer(state: OrderProviderState, action: OrderDispatch) {
  switch (action.type) {
    case OrderAction.SET_ORDERS: {
      return {
        orders: action.orders.reduce((state, order) => {
          state[order.id] = order;
          return state;
        }, state.orders),
      };
    }

    case OrderAction.FAILED_FETCH: {
      console.warn("Failed to update context with order.");
      return {
        ...state,
      };
    }

    default: {
      console.error(`Action of type ${action.type} does not exist within OrderProvider.`);
      return { ...state };
    }
  }
}
const OrderProvider = ({ children }: { children?: ReactNode }) => {
  const [state, dispatch] = useReducer(orderReducer, {
    orders: {},
  });

  const value = {
    state,
    dispatch,
    getOrders,
  };

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
};

export const useOrders = (): OrderContext => {
  const context = useContext(OrderContext);
  if (context == undefined) {
    throw new Error("useOrders must be used within an OrderProvider.");
  }
  return context;
};

export default OrderProvider;
